import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import {
  EERadioGroup,
  LoadingSpinner,
  Button,
  OptionsIndicator,
  EmployEngageLogo,
  GET_OPEN_FORMS_FOR_EMPLOYEE_AND_COMPANY_NO_AUTH,
} from '@employ-engage/common-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { RadioOption } from '@employ-engage/data';
import FindEmployeeForm from '../find-employee-form/find-employee-form';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

/* eslint-disable-next-line */
export interface SurveyGreetingProps {}

/** An open form as the server lists it: `extra` carries its template's settings. */
type FormOption = RadioOption & {
  extra: {
    formTemplateVersion: {
      isAnonymous?: boolean | null;
      isInfinitelyRespondable?: boolean | null;
      isPasscodeRequired?: boolean | null;
    };
  };
};

export function SurveyGreeting(props: SurveyGreetingProps) {
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isFormsAvailable, setIsFormsAvailable] = useState(false);
  const [isSelectedFormAnonymous, setIsSelectedFormAnonymous] = useState(false);
  const [formOptions, setFormOptions] = useState<FormOption[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const apolloClient = useApolloClient();
  const { pathname } = useLocation();

  const { handleSubmit, setFieldValue, values, errors, touched } = useFormik({
    initialValues: {
      formId: '',
      employeeId: '', // Only used if form is not anonymous
    },
    onSubmit: (values, { setErrors }) => {
      const friendlyCompanyId = pathname.split('/')[1];
      if (isSelectedFormAnonymous) {
        navigate(
          `/${friendlyCompanyId}/form/${values.formId}/employee/ANONYMOUS`
        );
      } else {
        navigate(
          `/${friendlyCompanyId}/form/${values.formId}/employee/${values.employeeId}`
        );
      }
    },
    validationSchema: Yup.object({
      formId: Yup.string().required('Required'),
    }),
  });

  const onSearchForForms = async (
    employeeId: string,
    setErrors: (arg0: { employeeId: string }) => void
  ) => {
    setFieldValue('employeeId', employeeId);
    setIsLoadingForms(true);
    const friendlyCompanyId = pathname.split('/')[1];
    if (!friendlyCompanyId) return;

    if (!employeeId) {
      setErrors({
        employeeId: 'Required - Employee ID can be found on your badge',
      });
      setIsLoadingForms(false);
      return;
    }

    const runQuery = async () => {
      const {
        data: { openFormOptionsNoAuth },
      } = await apolloClient.query({
        query: GET_OPEN_FORMS_FOR_EMPLOYEE_AND_COMPANY_NO_AUTH,
        variables: { friendlyCompanyId, employeeId },
        fetchPolicy: 'network-only',
      });
      return openFormOptionsNoAuth;
    };

    try {
      const openFormOptionsNoAuth = await runQuery();
      setFormOptions(openFormOptionsNoAuth);
      setIsFormsAvailable(true);
      setIsLoadingForms(false);
    } catch (error: any) {
      const isSessionError =
        error?.message?.includes('try refresh token') ||
        error?.message?.includes('UNAUTHORISED') ||
        error?.statusCode === 401;

      if (isSessionError) {
        try {
          // supertokens-web-js already attempted refresh and cleared the stale
          // session, so retrying without any further action should succeed
          const openFormOptionsNoAuth = await runQuery();
          setFormOptions(openFormOptionsNoAuth);
          setIsFormsAvailable(true);
          setIsLoadingForms(false);
          return;
        } catch {
          // fall through to generic error
        }
      }

      setErrors({ employeeId: t('Employee ID not found') });
      setIsLoadingForms(false);
    }
  };

  const handleFormSelect = (value: any) => {
    const form = formOptions.find((form) => {
      return form.id === value;
    });
    const isSelectedFormAnonymous =
      !!form?.extra?.formTemplateVersion?.isAnonymous;
    setIsSelectedFormAnonymous(isSelectedFormAnonymous);
    setFieldValue('formId', value);
  };

  return (
    <div className="px-10 sm:px-14">
      <div className="flex justify-center">
        <EmployEngageLogo size="large" />
      </div>
      <div className="flex justify-center">
        <h1 className="text-center text-lg sm:text-4xl font-bold">
          {t('Welcome!')}
        </h1>
      </div>
      <div className="flex justify-center h-[80vh] py-10">
        <div className="max-h-[60vh] overflow-y-auto">
          <FindEmployeeForm
            setFormOptions={setFormOptions}
            onSearchForForms={onSearchForForms}
            setIsFormsAvailable={setIsFormsAvailable}
          />
          <form onSubmit={handleSubmit} autoComplete="off">
            {values.employeeId && isFormsAvailable && (
              <>
                <div className="mb-10 flex flex-col justify-center items-center">
                  <h2 className="text-center text-md sm:text-3xl font-bold mb-10">
                    {t('Please select a form to begin')}
                  </h2>
                  {isLoadingForms ? (
                    <LoadingSpinner spinnerId="survey-greeting-loading" />
                  ) : formOptions?.length > 0 ? (
                    <EERadioGroup
                      isColumn
                      onChange={handleFormSelect}
                      errors={errors.formId}
                      touched={!!touched.formId}
                      value={values.formId}
                      options={formOptions}
                      mapOptionToLabel={(form: RadioOption) => {
                        // Every option given here is a FormOption; the
                        // radio group only knows them as RadioOptions.
                        const {
                          extra: { formTemplateVersion: templateVersion },
                        } = form as FormOption;

                        const options = [
                          {
                            activeColor: 'text-orange-500',
                            isActive: templateVersion.isAnonymous || false,
                            icon: 'fa-question',
                            label: t('Anonymous'),
                          },
                          {
                            activeColor: 'text-purple-500',
                            isActive:
                              templateVersion.isInfinitelyRespondable || false,
                            icon: 'fa-infinity',
                            label: t('Infinite Responses'),
                          },
                          {
                            activeColor: 'text-blue-500',
                            isActive:
                              templateVersion.isPasscodeRequired || false,
                            icon: 'fa-key',
                            label: t('Passcode Required'),
                          },

                        ];

                        return (
                          <div className="flex gap-2 items-center justify-center">
                            <span>{form.label}</span>
                            <OptionsIndicator options={options} />
                          </div>
                        );
                      }}
                    />
                  ) : (
                    <div>{t('no-forms-available')}</div>
                  )}
                </div>

                <div className="flex justify-center items-center text-xl sm:text-4xl">
                  <Button
                    type="submit"
                    disabled={!values.formId}
                    variant="primary"
                  >
                    {t('Submit')}
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default SurveyGreeting;
